import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Chip, Alert, CircularProgress, LinearProgress,
  InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Popover, Slider,
} from '@mui/material';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserOutlined';
import CloudUploadRoundedIcon from '@mui/icons-material/FileUploadOutlined';
import DnsRoundedIcon from '@mui/icons-material/DnsOutlined';
import ImageRoundedIcon from '@mui/icons-material/ImageOutlined';
import BusinessRoundedIcon from '@mui/icons-material/BusinessOutlined';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffOutlined';
import KeyRoundedIcon from '@mui/icons-material/KeyOutlined';
import InsertDriveFileRoundedIcon from '@mui/icons-material/DescriptionOutlined';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import api, { authService } from '../services/api';
import { HeaderIOS } from './shared/MenuLayout';
import './shared/PharmacyUI.css';
import './ComprobantesPago.css';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';

/*
  Economía → Configuración: línea visual compartida con Productos y Comprobantes.
  · Empresa emisora (solo lectura)
  · Certificado digital: detalle del sujeto/emisor, vigencia y carga de uno nuevo (con contraseña)
  · OSE (Nubefact): URL, usuario y contraseña (la contraseña nunca viaja al navegador)
  · Logo de la cabecera del comprobante impreso
  Solo un ADMINISTRADOR puede guardar (el backend lo vuelve a validar).
*/

const C = {
  blue: '#548EE6', green: '#59AE88', greenDark: '#287B66', orange: '#D99C55', red: '#D66F7C', indigo: '#9380CD',
  gray: '#7B879B', grayLight: '#F2F2F7', bg: '#F3F5FA', ink: '#253449', sep: 'rgba(60,60,67,0.13)',
};

// Tema local con los tokens de Datafarma: el tema global de Integra es morado (#6610F5) y radio 8.
const temaDatafarma = outer => createTheme(outer, {
  palette: {
    primary: { main: '#347ED4' }, secondary: { main: '#9380CD' }, error: { main: '#C5505E' },
    warning: { main: '#AD752B' }, success: { main: '#287B66' }, info: { main: '#548EE6' },
    text: { primary: '#202938', secondary: '#667085' },
  },
  typography: { fontFamily: ['-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(',') },
  shape: { borderRadius: 14 },
  components: { MuiTextField: { defaultProps: { size: 'small' } }, MuiButton: { styleOverrides: { root: { borderRadius: 10, textTransform: 'none', fontWeight: 600 } } } },
});

const OSE_PRESETS = [
  { label: 'Nubefact · DEMO', url: 'https://demo-ose.nubefact.com/ol-ti-itcpe/billService?wsdl', demo: true },
  { label: 'Nubefact · PRODUCCIÓN', url: 'https://ose.nubefact.com/ol-ti-itcpe/billService?wsdl', demo: false },
];

const campo = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#FFFFFF' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DFE5EE' },
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: `0 0 0 2px ${C.blue}22` },
};

/* ── Color del comprobante A4: misma paleta que PaletaCpe (backend) ─────── */
const GRIS_A4 = '#333333';
const PRESETS_A4 = [
  { n: 'Grafito', c: '#333333' }, { n: 'Azul niebla', c: '#617B99' }, { n: 'Eucalipto', c: '#56817D' },
  { n: 'Salvia', c: '#74816C' }, { n: 'Lavanda', c: '#80748F' }, { n: 'Arcilla', c: '#A17D69' }, { n: 'Medianoche', c: '#364356' },
];
const hexValido = h => /^#[0-9A-Fa-f]{6}$/.test((h || '').trim());
function paletaA4(hex) {
  if (!hexValido(hex) || hex.toUpperCase() === GRIS_A4)
    return { fuerte: '#333333', suave: '#EDEDED', etiqueta: '#eeeeee', fila: '#f7f7f7', textoFuerte: '#ffffff', acento: '#374151', acentoTitulo: '#333333' };
  const h = hex.trim().toUpperCase();
  const [r, g, b] = [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
  const mezcla = w => '#' + [r, g, b].map(v => Math.round(v + (255 - v) * w).toString(16).padStart(2, '0')).join('').toUpperCase();
  const claro = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 > 0.6;
  const acento = '#111827';   // textos casi negros (como el TOTAL); el color va en bandas y fondos
  return { fuerte: h, suave: mezcla(0.86), etiqueta: mezcla(0.90), fila: mezcla(0.95), textoFuerte: claro ? '#111111' : '#FFFFFF', acento, acentoTitulo: acento };
}

function colorAHsl(hex) {
  const [r,g,b] = [1,3,5].map(i => parseInt(hex.slice(i,i+2),16)/255);
  const max=Math.max(r,g,b), min=Math.min(r,g,b), d=max-min, l=(max+min)/2;
  let h=0;
  if(d) h=(max===r ? ((g-b)/d+6)%6 : max===g ? (b-r)/d+2 : (r-g)/d+4)*60;
  return [h,d ? d/(1-Math.abs(2*l-1))*100 : 0,l*100];
}
function hslAColor([h,s,l]) {
  s/=100; l/=100;
  const a=s*Math.min(l,1-l);
  const canal=n=>{const k=(n+h/30)%12;return Math.round(255*(l-a*Math.max(-1,Math.min(k-3,9-k,1)))).toString(16).padStart(2,'0');};
  return ('#'+canal(0)+canal(8)+canal(4)).toUpperCase();
}

const fecha = d => (d ? new Date(d).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const diasHasta = d => (d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null);
const errorMsg = (e, def) => e?.response?.data?.message || def;

// "E=demo@llama.pe, CN=..., OU=DNI 9 RUC 10..., O=..., L=LIMA, S=LIMA, C=PE" → { CN:[..], OU:[..], ... }
function parseDN(dn) {
  const out = {};
  (dn || '').split(/,\s*(?=(?:OID\.[\d.]+|[A-Za-z]+)=)/).forEach(p => {
    const i = p.indexOf('=');
    if (i <= 0) return;
    const k = p.slice(0, i).trim().toUpperCase();
    const v = p.slice(i + 1).trim().replace(/^"(.*)"$/, '$1');
    (out[k] = out[k] || []).push(v);
  });
  return out;
}
const uno = (dn, ...keys) => { for (const k of keys) if (dn[k]?.length) return dn[k].join(' · '); return ''; };

/* ── piezas visuales ─────────────────────────────────────────────────────── */
const Seccion = ({ icon, color, titulo, sub, chip, children, className }) => (
  <Paper component="section" className={className} elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: '20px', border: '1px solid #E2E7EF', mb: 0, minWidth:0, boxShadow:'0 4px 18px #243A5705' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap:'wrap', gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#F3F5F8', color, display: 'grid', placeItems: 'center', flexShrink: 0, '& svg': { fontSize: 20 } }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="h2" sx={{ fontWeight: 650, fontSize: '.94rem', color: C.ink }}>{titulo}</Typography>
        {sub && <Typography variant="body2" sx={{ color: C.gray, mt: .4, fontSize:12, lineHeight:1.6 }}>{sub}</Typography>}
      </Box>
      {chip}
    </Box>
    {children}
  </Paper>
);

const Estado = ({ texto, color }) => (
  <Chip size="small" label={texto} sx={{ fontWeight: 600, height: 24, bgcolor: `${color}12`, color, border: `1px solid ${color}30`, borderRadius: '7px', flexShrink: 0 }} />
);

const Dato = ({ k, v, mono }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '.7rem', fontWeight: 700, color: C.gray, letterSpacing: '.01em' }}>{k}</Typography>
    <Typography sx={{ fontSize: '.88rem', fontWeight: 600, color: v ? C.ink : C.gray, fontFamily: mono ? 'ui-monospace, SFMono-Regular, Consolas, monospace' : 'inherit', overflowWrap: 'anywhere', mt: .2 }}>
      {v || '—'}
    </Typography>
  </Box>
);

const Mensaje = ({ m, onClose }) => (m ? <Alert severity={m.tipo} onClose={onClose} sx={{ borderRadius: '10px', mb: 2 }}>{m.texto}</Alert> : null);

/* ── pantalla ────────────────────────────────────────────────────────────── */
export default function ConfiguracionEconomia() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const nf = user?.nombreCompleto || user?.usuario || '';
  const logout = () => { authService.logout(); navigate('/login'); };

  const [info, setInfo] = useState(null);
  const [esAdmin, setEsAdmin] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');

  const [cert, setCert] = useState({ archivo: null, clave: '' });
  const [verClave, setVerClave] = useState(false);
  const [certBusy, setCertBusy] = useState(false);
  const [certMsg, setCertMsg] = useState(null);
  const [confirmarRuc, setConfirmarRuc] = useState(false);
  const certInput = useRef(null);

  const [ose, setOse] = useState({ url: '', usuario: '', password: '' });
  const [verOse, setVerOse] = useState(false);
  const [oseBusy, setOseBusy] = useState(false);
  const [oseMsg, setOseMsg] = useState(null);
  const [oseConfirm, setOseConfirm] = useState(false);

  const [logoUrl, setLogoUrl] = useState('');
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoMsg, setLogoMsg] = useState(null);
  const logoInput = useRef(null);

  const [color, setColor] = useState(GRIS_A4);
  const [paletaAnchor, setPaletaAnchor] = useState(null);
  const [tonoHsl, setTonoHsl] = useState([0,0,20]);
  const [hexTexto, setHexTexto] = useState(GRIS_A4);
  const [rgbTexto, setRgbTexto] = useState(['51','51','51']);
  const sincronizarCamposColor = hex => {
    setHexTexto(hex);
    setRgbTexto([1,3,5].map(i=>String(parseInt(hex.slice(i,i+2),16))));
  };
  const aplicarColorEscrito = hex => {
    setColor(hex); setTonoHsl(colorAHsl(hex)); setColorMsg(null);
  };
  const cambiarHex = texto => {
    setHexTexto(texto);
    const limpio=texto.trim().replace(/^#/, '');
    if (/^[0-9a-fA-F]{6}$/.test(limpio)) {
      const hex='#'+limpio.toUpperCase(); aplicarColorEscrito(hex);
      setRgbTexto([1,3,5].map(i=>String(parseInt(hex.slice(i,i+2),16))));
    }
  };
  const cambiarRgb = (indice,texto) => {
    const valores=rgbTexto.map((v,i)=>i===indice?texto:v); setRgbTexto(valores);
    if(valores.every(v=>/^\d{1,3}$/.test(v) && Number(v)<=255)) {
      const hex=('#'+valores.map(v=>Number(v).toString(16).padStart(2,'0')).join('')).toUpperCase();
      aplicarColorEscrito(hex); setHexTexto(hex);
    }
  };
  const cambiarTono = (indice, valor) => {
    const siguiente = tonoHsl.map((v,i) => i === indice ? valor : v);
    setTonoHsl(siguiente); setColor(hslAColor(siguiente)); sincronizarCamposColor(hslAColor(siguiente)); setColorMsg(null);
  };
  const [colorBusy, setColorBusy] = useState(false);
  const [colorMsg, setColorMsg] = useState(null);

  const cargarLogo = useCallback(async conLogo => {
    if (!conLogo) { setLogoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return ''; }); return; }
    try {
      const r = await api.get('/economia/configuracion/logo', { responseType: 'blob' });
      setLogoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(r.data); });
    } catch { setLogoUrl(''); }
  }, []);

  const cargar = useCallback(async () => {
    setErrorCarga('');
    try {
      const r = await api.get('/economia/configuracion/resumen');
      if (!r.data?.success) { setErrorCarga(r.data?.message || 'No se pudo cargar la configuración.'); return; }
      const d = r.data.data;
      setInfo(d); setEsAdmin(Boolean(r.data.puedeEditar));
      setOse({ url: d.oseUrl || '', usuario: d.oseUsuario || '', password: '' });
      setColor(hexValido(d.colorPdf) ? d.colorPdf.toUpperCase() : GRIS_A4);
      await cargarLogo(d.tieneLogo);
    } catch (e) { setErrorCarga(errorMsg(e, 'No se pudo cargar la configuración.')); }
    finally { setCargando(false); }
  }, [cargarLogo]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => () => { if (logoUrl) URL.revokeObjectURL(logoUrl); }, [logoUrl]);

  /* certificado */
  const subirCert = async () => {
    if (!cert.archivo) { setCertMsg({ tipo: 'error', texto: 'Elige el archivo .pfx o .p12.' }); return; }
    if (!cert.clave) { setCertMsg({ tipo: 'error', texto: 'Ingresa la contraseña del certificado.' }); return; }
    setCertBusy(true); setCertMsg(null);
    try {
      const fd = new FormData();
      fd.append('archivo', cert.archivo);
      fd.append('clave', cert.clave);
      fd.append('confirmarRuc', confirmarRuc ? 'true' : 'false');
      const r = await api.post('/economia/configuracion/certificado', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      if (r.data?.success) {
        setCertMsg({ tipo: 'success', texto: r.data.message });
        setCert({ archivo: null, clave: '' }); setConfirmarRuc(false);
        if (certInput.current) certInput.current.value = '';
        await cargar();
      } else if (r.data?.requiereConfirmacion) {
        setConfirmarRuc(true); setCertMsg({ tipo: 'warning', texto: r.data.message });
      } else setCertMsg({ tipo: 'error', texto: r.data?.message || 'No se pudo guardar el certificado.' });
    } catch (e) { setCertMsg({ tipo: 'error', texto: errorMsg(e, 'No se pudo guardar el certificado.') }); }
    finally { setCertBusy(false); }
  };

  /* OSE */
  const guardarOse = async () => {
    setOseConfirm(false); setOseBusy(true); setOseMsg(null);
    try {
      const r = await api.post('/economia/configuracion/ose', { url: ose.url, usuario: ose.usuario, password: ose.password || null });
      if (r.data?.success) { setOseMsg({ tipo: 'success', texto: r.data.message }); await cargar(); }
      else setOseMsg({ tipo: 'error', texto: r.data?.message || 'No se pudo guardar el OSE.' });
    } catch (e) { setOseMsg({ tipo: 'error', texto: errorMsg(e, 'No se pudo guardar el OSE.') }); }
    finally { setOseBusy(false); }
  };
  const oseCambio = info && (ose.url.trim() !== (info.oseUrl || '') || ose.usuario.trim() !== (info.oseUsuario || '') || ose.password !== '');

  /* logo */
  const subirLogo = async file => {
    if (!file) return;
    setLogoBusy(true); setLogoMsg(null);
    try {
      const fd = new FormData(); fd.append('archivo', file);
      const r = await api.post('/economia/configuracion/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      if (r.data?.success) { setLogoMsg({ tipo: 'success', texto: r.data.message }); await cargar(); }
      else setLogoMsg({ tipo: 'error', texto: r.data?.message || 'No se pudo guardar el logo.' });
    } catch (e) { setLogoMsg({ tipo: 'error', texto: errorMsg(e, 'No se pudo guardar el logo.') }); }
    finally { setLogoBusy(false); if (logoInput.current) logoInput.current.value = ''; }
  };
  const quitarLogo = async () => {
    setLogoBusy(true); setLogoMsg(null);
    try {
      const r = await api.delete('/economia/configuracion/logo');
      if (r.data?.success) { setLogoMsg({ tipo: 'success', texto: r.data.message }); await cargar(); }
      else setLogoMsg({ tipo: 'error', texto: r.data?.message || 'No se pudo quitar el logo.' });
    } catch (e) { setLogoMsg({ tipo: 'error', texto: errorMsg(e, 'No se pudo quitar el logo.') }); }
    finally { setLogoBusy(false); }
  };

  /* color A4 */
  const guardarColor = async valor => {
    setColorBusy(true); setColorMsg(null);
    try {
      const r = await api.post('/economia/configuracion/color', { color: valor });
      if (r.data?.success) {
        setColorMsg({ tipo: 'success', texto: r.data.message });
        setInfo(i => ({ ...i, colorPdf: r.data.colorPdf || '' }));
        setColor(hexValido(r.data.colorPdf) ? r.data.colorPdf : GRIS_A4);
      } else setColorMsg({ tipo: 'error', texto: r.data?.message || 'No se pudo guardar el color.' });
    } catch (e) { setColorMsg({ tipo: 'error', texto: errorMsg(e, 'No se pudo guardar el color.') }); }
    finally { setColorBusy(false); }
  };
  const colorGuardado = hexValido(info?.colorPdf) ? info.colorPdf.toUpperCase() : GRIS_A4;
  const colorCambio = hexValido(color) && color.toUpperCase() !== colorGuardado;
  const pal = paletaA4(color);
  const presetColor = PRESETS_A4.find(p => p.c === colorGuardado);

  /* derivados */
  const sujeto = useMemo(() => parseDN(info?.certSujeto), [info?.certSujeto]);
  const emisor = useMemo(() => parseDN(info?.certEmisor), [info?.certEmisor]);
  const dias = diasHasta(info?.certFin);
  const total = info?.certInicio && info?.certFin ? (new Date(info.certFin) - new Date(info.certInicio)) / 86400000 : null;
  const usado = total && dias !== null ? Math.min(100, Math.max(0, 100 - (dias / total) * 100)) : 0;
  const estCert = !info?.tieneCert ? { t: 'Sin certificado', c: C.red }
    : dias === null ? { t: 'Sin fecha', c: C.gray }
    : dias <= 0 ? { t: 'Vencido', c: C.red }
    : dias <= 30 ? { t: `Vence en ${dias} d`, c: C.orange }
    : { t: `Vigente · ${dias} d`, c: C.greenDark };
  const preset = OSE_PRESETS.find(p => p.url === (info?.oseUrl || ''));
  const estOse = !info?.oseUrl ? { t: 'Sin configurar', c: C.red }
    : preset ? { t: preset.demo ? 'DEMO' : 'Producción', c: preset.demo ? C.orange : C.greenDark }
    : { t: 'Personalizado', c: C.blue };
  const ubicacion = [uno(sujeto, 'L'), uno(sujeto, 'S', 'ST'), uno(sujeto, 'C')].filter(Boolean).join(', ');

  return (
    <ThemeProvider theme={temaDatafarma}>
    <Box className="pharmacy-page cpe-page economy-settings" sx={{ minHeight: '100vh', bgcolor: C.bg }}>
      <HeaderIOS titulo="Economía" subtitulo="Configuración" onHome={() => navigate('/economia')} onLogout={logout}
        nombreCompleto={nf} avatarLetra={nf.charAt(0).toUpperCase()} />

      <Box className="pharmacy-content cpe-content">
        <Box className="cpe-intro"><Typography className="cpe-eyebrow">Configuración · Economía</Typography><Typography component="h1">Tu emisión, bien configurada</Typography><Typography>Administra el certificado, la conexión y la identidad de tus comprobantes.</Typography></Box>

        {cargando && <LinearProgress sx={{ borderRadius: 2 }} />}
        {!cargando && errorCarga && <Alert severity="error" sx={{ borderRadius: '10px' }}>{errorCarga}</Alert>}

        {!cargando && !errorCarga && info && (
          <>
            {!esAdmin && <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>Solo un administrador puede modificar estos datos.</Alert>}

            <div className="eco-settings-grid">
            {/* ── Empresa ────────────────────────────────────────────── */}
            <Seccion className="eco-company" icon={<BusinessRoundedIcon />} color={C.blue} titulo={info.razonSocial || 'Empresa emisora'} sub={`RUC ${info.ruc}`}
              chip={<Box sx={{ display: 'flex', gap: .8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Estado texto={`Certificado: ${estCert.t}`} color={estCert.c} />
                <Estado texto={`OSE: ${estOse.t}`} color={estOse.c} />
              </Box>}>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' } }}>
                <Dato k="Dirección fiscal" v={info.direccion} />
                <Dato k="Ubigeo" v={info.ubigeo} mono />
                <Dato k="Distrito" v={[info.distrito, info.provincia, info.departamento].filter(Boolean).join(' · ')} />
              </Box>
            </Seccion>

            {/* ── Certificado ────────────────────────────────────────── */}
            <Seccion className="eco-certificate" icon={<VerifiedUserRoundedIcon />} color={C.greenDark} titulo="Certificado digital"
              sub="La firma digital que identifica a tu entidad."
              chip={<Estado texto={estCert.t} color={estCert.c} />}>

              {info.certDetalleAviso && <Alert severity="warning" sx={{mb:2,borderRadius:'10px'}}>{info.certDetalleAviso}</Alert>}
              {info.tieneCert ? (
                <Box sx={{ borderRadius: '16px', p: { xs: 2, sm: 2.5 }, mb: 2.5, color: '#fff', position: 'relative', overflow: 'hidden',
                  background: 'linear-gradient(120deg, #287B66, #318773)' }}>
                  <Box sx={{ position: 'absolute', right: -30, top: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.06)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, position: 'relative' }}>
                    <KeyRoundedIcon sx={{ fontSize: 18, opacity: .85 }} />
                    <Typography sx={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', opacity: .85 }}>Firma digital</Typography>
                    <Box sx={{ flex: 1 }} />
                    {info.certNombre && <Typography sx={{ fontSize: '.72rem', opacity: .75, overflowWrap:'anywhere', maxWidth:'50%' }}>{info.certNombre}</Typography>}
                  </Box>
                  <Typography sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, fontWeight: 700, lineHeight: 1.3, position: 'relative', overflowWrap: 'anywhere' }}>
                    {uno(sujeto, 'CN') || 'Titular no disponible'}
                  </Typography>
                  <Typography sx={{ fontSize: '.85rem', opacity: .85, mt: .3, overflowWrap: 'anywhere' }}>{uno(sujeto, 'O')}</Typography>

                  <Box sx={{ display: 'grid', gap: 1.5, mt: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, position: 'relative',
                    '& .k': { fontSize: '.66rem', fontWeight: 700, opacity: .7, textTransform: 'uppercase', letterSpacing: '.05em' },
                    '& .v': { fontSize: '.84rem', fontWeight: 600, overflowWrap: 'anywhere' } }}>
                    <Box><div className="k">Documento</div><div className="v">{uno(sujeto, 'OU', 'SERIALNUMBER') || '—'}</div></Box>
                    <Box><div className="k">Correo</div><div className="v">{uno(sujeto, 'E', 'EMAILADDRESS') || '—'}</div></Box>
                    <Box><div className="k">Ubicación</div><div className="v">{ubicacion || '—'}</div></Box>
                    <Box><div className="k">Emitido por</div><div className="v">{uno(emisor, 'CN', 'O') || '—'}</div></Box>
                    <Box sx={{ gridColumn: { sm: '1 / -1' } }}><div className="k">Número de serie</div><div className="v" style={{ fontFamily: 'monospace' }}>{info.certSerie || '—'}</div></Box>
                  </Box>

                  <Box sx={{ mt: 2.2, position: 'relative' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '.74rem', opacity: .85, mb: .6 }}>
                      <span>Desde {fecha(info.certInicio)}</span><span>Vence {fecha(info.certFin)}</span>
                    </Box>
                    <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,.18)', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${usado}%`, borderRadius: 3, bgcolor: dias !== null && dias <= 30 ? '#FFB340' : '#7EE0A1' }} />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>Todavía no hay certificado. Sin él no se puede firmar.</Alert>
              )}

              {info.actualizado && (
                <Typography variant="caption" sx={{ color: C.gray, display: 'block', mb: 2 }}>
                  Última actualización: {fecha(info.actualizado)}{info.actualizadoPor ? ` · ${info.actualizadoPor}` : ''}
                </Typography>
              )}

              {esAdmin && (
                <>
                  <Typography sx={{ fontWeight: 700, fontSize: '.9rem', mb: 1.2 }}>Subir nuevo certificado</Typography>
                  <Mensaje m={certMsg} onClose={() => setCertMsg(null)} />
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, alignItems: 'stretch' }}>
                    <Button component="label" variant="outlined" startIcon={cert.archivo ? <InsertDriveFileRoundedIcon /> : <CloudUploadRoundedIcon />}
                      sx={{ borderRadius: '10px', borderStyle: 'dashed', justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600, minHeight: 40, px: 2, overflow: 'hidden' }}>
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cert.archivo ? cert.archivo.name : 'Elegir archivo .pfx / .p12'}
                      </Box>
                      <input ref={certInput} hidden type="file" accept=".pfx,.p12"
                        onChange={e => { setCert(c => ({ ...c, archivo: e.target.files?.[0] || null })); setCertMsg(null); setConfirmarRuc(false); }} />
                    </Button>
                    <TextField sx={campo} label="Contraseña del certificado" type={verClave ? 'text' : 'password'} autoComplete="new-password"
                      value={cert.clave} onChange={e => { setCert(c => ({ ...c, clave: e.target.value })); setCertMsg(null); setConfirmarRuc(false); }}
                      InputProps={{ endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'} onClick={() => setVerClave(v => !v)} edge="end" size="small">
                            {verClave ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>) }} />
                    <Button variant="contained" disableElevation onClick={subirCert} disabled={certBusy || !cert.archivo}
                      color={confirmarRuc ? 'warning' : 'primary'}
                      sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, gridColumn:'1 / -1', minHeight: 42, px: 3, whiteSpace: 'nowrap' }}>
                      {certBusy ? <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> Validando…</> : confirmarRuc ? 'Confirmar y guardar' : 'Subir certificado'}
                    </Button>
                  </Box>
                  <Typography variant="caption" sx={{ color: C.gray, display: 'block', mt: 1 }}>
                    Antes de guardar se verifica la contraseña, la llave privada, el RUC y la vigencia. El certificado anterior queda respaldado.
                    {' '}<Box component="span" role="link" tabIndex={0} onClick={() => navigate('/economia/cotizar-certificado')}
                      onKeyDown={e => { if (e.key === 'Enter') navigate('/economia/cotizar-certificado'); }}
                      sx={{ color: C.blue, cursor: 'pointer', fontWeight: 600 }}>¿Necesitas renovarlo?</Box>
                  </Typography>
                </>
              )}
            </Seccion>

            {/* ── OSE ────────────────────────────────────────────────── */}
            <Seccion className="eco-connection" icon={<DnsRoundedIcon />} color={C.indigo} titulo="Conexión OSE"
              sub="Conexión para validar tus comprobantes electrónicos."
              chip={<Estado texto={estOse.t} color={estOse.c} />}>
              <Mensaje m={oseMsg} onClose={() => setOseMsg(null)} />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.8 }}>
                {OSE_PRESETS.map(p => {
                  const sel = ose.url === p.url;
                  return (
                    <Chip key={p.url} label={p.label} onClick={esAdmin ? () => setOse(o => ({ ...o, url: p.url })) : undefined}
                      sx={{ fontWeight: 600, borderRadius: '10px', bgcolor: sel ? `${C.blue}22` : C.grayLight, color: sel ? C.blue : C.ink,
                        border: `1px solid ${sel ? C.blue + '55' : 'transparent'}` }} />
                  );
                })}
              </Box>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, '& > :first-child':{gridColumn:'1 / -1'}, '& > *': { minWidth: 0 } }}>
                <TextField sx={campo} label="URL del servicio (billService)" value={ose.url} disabled={!esAdmin}
                  onChange={e => setOse(o => ({ ...o, url: e.target.value }))} />
                <TextField sx={campo} label="Usuario OSE" value={ose.usuario} disabled={!esAdmin}
                  onChange={e => setOse(o => ({ ...o, usuario: e.target.value }))} />
                <TextField sx={campo} label="Contraseña OSE" type={verOse ? 'text' : 'password'} autoComplete="new-password" value={ose.password} disabled={!esAdmin}
                  placeholder={info.tieneOsePassword ? '•••••• (guardada)' : ''}
                  helperText={info.tieneOsePassword ? 'Déjala vacía para no cambiarla' : 'Sin contraseña guardada'}
                  onChange={e => setOse(o => ({ ...o, password: e.target.value }))}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label={verOse ? 'Ocultar contraseña' : 'Mostrar contraseña'} onClick={() => setVerOse(v => !v)} edge="end" size="small" disabled={!esAdmin}>
                        {verOse ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>) }} />
              </Box>
              {esAdmin && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button variant="contained" disableElevation disabled={!oseCambio || oseBusy} onClick={() => setOseConfirm(true)}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3 }}>
                    {oseBusy ? <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> Guardando…</> : 'Guardar OSE'}
                  </Button>
                </Box>
              )}
            </Seccion>

            {/* ── Logo ───────────────────────────────────────────────── */}
            <Seccion className="eco-brand" icon={<ImageRoundedIcon />} color={C.orange} titulo="Identidad del comprobante"
              sub="Logo que aparecerá en tus comprobantes."
              chip={<Estado texto={info.tieneLogo ? 'Personalizado' : 'Por defecto'} color={info.tieneLogo ? C.greenDark : C.gray} />}>
              <Mensaje m={logoMsg} onClose={() => setLogoMsg(null)} />
              <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: '1fr', alignItems: 'center' }}>
                <Box aria-label="Logo del comprobante"
                  sx={{ border: `1px solid ${C.sep}`, borderRadius: '12px', p: 2.5, bgcolor: '#FAFBFD', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 96 }}>
                  {logoUrl
                    ? <Box component="img" src={logoUrl} alt="Logo actual" sx={{ maxHeight: 72, maxWidth: '100%', objectFit: 'contain', display: 'block' }} />
                    : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: C.gray, fontSize: 12 }}><ImageRoundedIcon fontSize="small" /> Logo por defecto</Box>}
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ color: C.gray, mb: 1.5 }}>
                    PNG o JPG de hasta 2 MB, horizontal y con fondo blanco o transparente.
                    {info.logoFecha ? ` Actualizado el ${fecha(info.logoFecha)}${info.logoPor ? ' · ' + info.logoPor : ''}.` : ''}
                  </Typography>
                  {esAdmin && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button component="label" variant="contained" disableElevation startIcon={logoBusy ? null : <CloudUploadRoundedIcon />} disabled={logoBusy}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                        {logoBusy ? <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> Guardando…</> : info.tieneLogo ? 'Cambiar logo' : 'Subir logo'}
                        <input ref={logoInput} hidden type="file" accept="image/png,image/jpeg" onChange={e => subirLogo(e.target.files?.[0])} />
                      </Button>
                      {info.tieneLogo && (
                        <Tooltip title="Vuelve a usar el logo de siempre">
                          <span>
                            <Button variant="outlined" disabled={logoBusy} onClick={quitarLogo} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, color: C.ink, borderColor: C.sep, bgcolor: '#fff', px: 2, '&:hover': { borderColor: '#B7C1CE', bgcolor: '#F5F7FA' } }}>
                              Usar logo por defecto
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Seccion>

            {/* ── Color del comprobante A4 ───────────────────────────── */}
            <Seccion className="eco-color" icon={<PaletteOutlinedIcon />} color={colorGuardado === GRIS_A4 ? C.gray : colorGuardado} titulo="Color del comprobante A4"
              sub="Facturas, boletas y notas en A4. El ticket sigue en blanco y negro."
              chip={<Estado texto={colorGuardado === GRIS_A4 ? 'Grafito' : (presetColor ? presetColor.n : 'Personalizado')} color={colorGuardado === GRIS_A4 ? C.gray : C.greenDark} />}>
              <Mensaje m={colorMsg} onClose={() => setColorMsg(null)} />
              <Box className="eco-color-grid">
                {/* Selector */}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '.72rem', fontWeight: 700, color: C.gray, mb: 1 }}>Paleta de colores</Typography>
                  <Box className="eco-swatches" role="group" aria-label="Colores del comprobante">
                    {PRESETS_A4.map(p => {
                      const sel = color.toUpperCase() === p.c;
                      return (
                        <button key={p.c} type="button" aria-pressed={sel} disabled={!esAdmin || colorBusy}
                          className={`eco-swatch${sel ? ' is-sel' : ''}`} onClick={() => { setColor(p.c); setColorMsg(null); }}>
                          <span className="eco-swatch-dot" style={{ background: p.c }} aria-hidden="true">{sel && <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="m5 12 4 4L19 6" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>}</span>
                          <span className="eco-swatch-name">{p.n}</span>
                        </button>
                      );
                    })}
                  </Box>

                  <Box className="eco-custom-color">
                    <Box component="button" type="button" className="eco-color-picker" disabled={!esAdmin || colorBusy}
                      aria-haspopup="dialog" aria-expanded={Boolean(paletaAnchor)} aria-controls={paletaAnchor ? 'cpe-color-panel' : undefined}
                      onClick={e=>{const actual=hexValido(color)?color:GRIS_A4;setTonoHsl(colorAHsl(actual));sincronizarCamposColor(actual);setPaletaAnchor(e.currentTarget);}}>
                      <span className="eco-custom-sample" style={{background:color}} aria-hidden="true" />
                      <span><strong>Color personalizado</strong><span>Elige un tono en la paleta</span></span>
                      <PaletteOutlinedIcon sx={{fontSize:19,color:C.gray,ml:'auto'}} />
                    </Box>
                    <Popover open={Boolean(paletaAnchor)} anchorEl={paletaAnchor} onClose={()=>setPaletaAnchor(null)}
                      anchorOrigin={{vertical:'bottom',horizontal:'left'}} transformOrigin={{vertical:'top',horizontal:'left'}}
                      transitionDuration={180} PaperProps={{id:'cpe-color-panel',role:'dialog','aria-label':'Color personalizado',sx:{mt:1,p:2.5,width:290,maxWidth:'calc(100vw - 32px)',boxSizing:'border-box',borderRadius:'16px',border:'1px solid #E5E9EF',boxShadow:'0 12px 40px #25344920',background:'#fff'}}}>
                      <Box sx={{display:'flex',alignItems:'center',gap:1.5,mb:2}}>
                        <Box sx={{width:36,height:36,borderRadius:'10px',bgcolor:color,border:'1px solid #0000000A'}} />
                        <Typography sx={{fontSize:14,fontWeight:600,flex:1}}>Color personalizado</Typography>
                        <IconButton size="small" aria-label="Cerrar paleta" onClick={()=>setPaletaAnchor(null)}><CloseRoundedIcon sx={{fontSize:18}} /></IconButton>
                      </Box>
                      {['Tono','Intensidad','Luminosidad'].map((nombre,i)=>(
                        <Box key={nombre} sx={{mb:1}}>
                          <Typography id={`cpe-color-label-${i}`} sx={{fontSize:12,color:'#667085',mb:.5}}>{nombre}</Typography>
                          <Slider aria-labelledby={`cpe-color-label-${i}`} value={tonoHsl[i]} min={0} max={i===0?360:100}
                            disabled={!esAdmin || colorBusy} onChange={(_,v)=>cambiarTono(i,v)} track={false}
                            sx={{color:'#657B94',height:8,'& .MuiSlider-rail':{opacity:1,background:i===0?'linear-gradient(to right,#e57373,#e5d573,#73bb83,#73c4cf,#7d86cc,#bf7dc0,#e57373)':i===1?`linear-gradient(to right,hsl(${tonoHsl[0]},0%,50%),hsl(${tonoHsl[0]},100%,50%))`:`linear-gradient(to right,#16191d,hsl(${tonoHsl[0]},${tonoHsl[1]}%,50%),#fff)`,border:'1px solid #0000000A'},'& .MuiSlider-thumb':{width:18,height:18,bgcolor:'#fff',border:'2px solid #8493A5',boxShadow:'0 1px 4px #00000015','&:hover,&.Mui-focusVisible,&.Mui-active':{boxShadow:'0 0 0 5px #8493A51A'}}}} />
                        </Box>
                      ))}
                      <Box sx={{pt:1.5,mt:.5,borderTop:'1px solid #EEF0F3'}}>
                        <TextField fullWidth size="small" label="HEX" placeholder="#617B99" value={hexTexto}
                          disabled={!esAdmin || colorBusy} onChange={e=>cambiarHex(e.target.value)}
                          error={!/^#?[0-9a-fA-F]{6}$/.test(hexTexto.trim())}
                          helperText={!/^#?[0-9a-fA-F]{6}$/.test(hexTexto.trim()) ? 'Escribe 6 caracteres, por ejemplo #617B99.' : 'Código con o sin #'}
                          inputProps={{maxLength:7,spellCheck:false,'aria-label':'Código hexadecimal'}}
                          sx={{'& input':{fontFamily:'ui-monospace, monospace',fontSize:13}}} />
                        <Box sx={{display:'grid',gridTemplateColumns:'repeat(3,minmax(0,1fr))',gap:1,mt:1.5}}>
                          {['R','G','B'].map((canal,i)=><TextField key={canal} size="small" label={canal} value={rgbTexto[i]}
                            disabled={!esAdmin || colorBusy} onChange={e=>cambiarRgb(i,e.target.value)}
                            error={!/^\d{1,3}$/.test(rgbTexto[i]) || Number(rgbTexto[i])>255}
                            inputProps={{inputMode:'numeric',maxLength:3,'aria-label':{R:'Rojo',G:'Verde',B:'Azul'}[canal]}} />)}
                        </Box>
                        <Typography sx={{fontSize:11,color:'#75808E',mt:.7}}>RGB · valores de 0 a 255</Typography>
                      </Box>
                      <Button fullWidth variant="outlined" onClick={()=>setPaletaAnchor(null)} sx={{mt:1,borderRadius:'10px',textTransform:'none'}}>Listo</Button>
                    </Popover>
                    <Typography role="status" sx={{fontSize:12,color:C.gray,mt:1.2}}>
                      {PRESETS_A4.find(p=>p.c===color.toUpperCase())?.n || 'Tono personalizado'} · {colorCambio ? 'Sin guardar' : 'Guardado'}
                    </Typography>
                  </Box>

                  {esAdmin && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      <Button variant="contained" disableElevation disabled={!colorCambio || colorBusy} onClick={() => guardarColor(color.toUpperCase())}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                        {colorBusy ? <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> Guardando…</> : 'Guardar color'}
                      </Button>
                      <Button variant="text" disabled={colorBusy || colorGuardado === GRIS_A4} onClick={() => guardarColor(null)}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, color: C.gray }}>
                        Volver al gris
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Vista previa con la misma paleta del PDF */}
                <Box className="eco-a4-preview" aria-label="Vista previa del comprobante A4">
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      {logoUrl
                        ? <Box component="img" src={logoUrl} alt="" sx={{ maxHeight: 30, maxWidth: '100%', objectFit: 'contain', display: 'block' }} />
                        : <Box sx={{ height: 26, width: 72, borderRadius: '6px', bgcolor: '#EEF1F5' }} />}
                      <Typography sx={{ fontSize: 10, fontWeight: 800, color: pal.acentoTitulo, mt: .8, overflowWrap: 'anywhere', lineHeight: 1.3 }}>{info.razonSocial}</Typography>
                    </Box>
                    <Box sx={{ bgcolor: pal.suave, borderTop: `3px solid ${pal.fuerte}`, px: 1.2, py: 1.2, textAlign: 'center', flexShrink: 0, minWidth: 116 }}>
                      <Typography sx={{ fontSize: 8.5, fontWeight: 800, color: '#111827' }}>RUC {info.ruc}</Typography>
                      <Box sx={{ height: '1px', bgcolor: pal.fuerte, my: .5 }} />
                      <Typography sx={{ fontSize: 8.5, fontWeight: 800, color: '#111827', lineHeight: 1.2 }}>FACTURA ELECTRÓNICA</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, mt: .6, color: pal.acento }}>F001 - 00000123</Typography>
                    </Box>
                  </Box>

                  <Box className="eco-a4-customer" sx={{ mt: 1.8 }}>
                    <Box>
                      <span className="eco-preview-caption">CLIENTE / RAZÓN SOCIAL</span>
                      <strong>CLIENTE DE EJEMPLO S.A.C.</strong>
                      <span>RUC: 20123456789</span>
                      <span className="eco-preview-muted">Av. Principal 123 · Lima</span>
                    </Box>
                    <Box className="eco-a4-meta">
                      <span>Fecha de emisión</span><span>23/10/2026</span>
                      <span>Hora</span><span>10:45:00</span>
                      <span>Forma de pago</span><span>CONTADO</span>
                      <span>Moneda</span><span>SOLES</span>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1.4, p: 1, bgcolor: '#F7F8FA', borderLeft: '2px solid #CCD5DF' }}>
                    <span className="eco-preview-caption">OBSERVACIONES</span>
                    <Box sx={{ mt: .4, fontSize: 9, color: '#344054' }}>Entrega coordinada con el cliente.</Box>
                  </Box>

                  <Box className="eco-a4-table" sx={{ mt: 1.2 }}>
                    <Box className="eco-a4-row" sx={{ bgcolor: pal.fuerte, color: pal.textoFuerte, fontWeight: 800 }}>
                      <span>CANT.</span><span>DESCRIPCIÓN</span><span className="r">SUBTOTAL</span>
                    </Box>
                    <Box className="eco-a4-row" sx={{ bgcolor: '#fff' }}><span>2</span><span>PARACETAMOL 500 MG TABLETA</span><span className="r">4.00</span></Box>
                    <Box className="eco-a4-row" sx={{ bgcolor: pal.fila }}><span>1</span><span>CONSULTA EXTERNA</span><span className="r">35.00</span></Box>
                    <Box className="eco-a4-row" sx={{ bgcolor: '#fff' }}><span>3</span><span>JERINGA DESCARTABLE 5 ML</span><span className="r">3.60</span></Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.4 }}>
                    <Box sx={{ width: '55%', minWidth: 150 }}>
                      <Box className="eco-preview-total"><span>GRAVADO (S/)</span><span>36.10</span></Box>
                      <Box className="eco-preview-total"><span>IGV 18% (S/)</span><span>6.50</span></Box>
                      <Box className="eco-preview-total" sx={{ bgcolor: pal.suave, py: 1, fontWeight: 800 }}><span>TOTAL (S/)</span><span>42.60</span></Box>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1.5, fontSize: 8, color: '#475467' }}><b>SON:</b> CUARENTA Y DOS Y 60/100 SOLES</Box>
                  <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #EEF0F3', fontSize: 8, color: '#8993A1' }}>Representación impresa de la factura electrónica · Vista ilustrativa</Box>
                </Box>
              </Box>
            </Seccion>
            </div>
          </>
        )}
      </Box>

      {/* Confirmación OSE: un dato mal puesto corta los envíos a SUNAT */}
      <Dialog open={oseConfirm} onClose={() => setOseConfirm(false)} PaperProps={{ sx: { borderRadius: '18px', maxWidth: 440 } }}>
        <DialogTitle sx={{fontWeight:700,fontSize:18,display:'flex',alignItems:'center',justifyContent:'space-between',gap:2}}>Actualizar conexión OSE<IconButton aria-label="Cerrar" onClick={()=>setOseConfirm(false)} sx={{width:32,height:32,minWidth:32,p:0,borderRadius:'50%',bgcolor:'#F1F4F8',flexShrink:0}}><CloseRoundedIcon sx={{fontSize:18}}/></IconButton></DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: C.gray }}>
            Los próximos comprobantes se enviarán con esta configuración. Si la URL, el usuario o la contraseña no son correctos,
            los envíos a SUNAT fallarán hasta que se corrija. Los datos anteriores quedan respaldados.
          </Typography>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: C.grayLight, borderRadius: '10px', fontSize: 13, overflowWrap: 'anywhere' }}>
            <div><b>URL:</b> {ose.url || '—'}</div>
            <div><b>Usuario:</b> {ose.usuario || '—'}</div>
            <div><b>Contraseña:</b> {ose.password ? 'se reemplaza' : 'se mantiene'}</div>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOseConfirm(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancelar</Button>
          <Button variant="contained" disableElevation onClick={guardarOse} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
      <style>{`
.economy-settings .eco-settings-grid{display:grid;grid-template-columns:minmax(0,1.12fr) minmax(0,1fr);gap:20px;align-items:start}
.economy-settings .eco-company{grid-column:1/-1;background:#fff}
.economy-settings .eco-certificate{grid-column:1;grid-row:2/4;height:100%;box-sizing:border-box}
.economy-settings .eco-connection{grid-column:2;grid-row:2}
.economy-settings .eco-brand{grid-column:2;grid-row:3}
.economy-settings .MuiButton-root{font-size:12px;border-radius:10px;min-height:40px}
.economy-settings .MuiInputBase-input{font-size:13px;min-height:0!important;padding:11px 12px}
.economy-settings .MuiOutlinedInput-root{min-height:40px}
.economy-settings .MuiButton-startIcon svg{font-size:18px}
.economy-settings .MuiIconButton-root{width:32px;height:32px;min-height:32px;min-width:32px;padding:6px;flex-shrink:0}
.economy-settings input::-ms-reveal{display:none}
.economy-settings .MuiInputLabel-root{font-size:13px}
.economy-settings .MuiChip-label{font-size:11px}
.economy-settings .eco-brand [aria-label="Vista previa de la cabecera del comprobante"]{background:#FAFBFD}
.economy-settings .eco-color{grid-column:1/-1}
.economy-settings .eco-color-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.15fr);gap:24px;align-items:start}
.economy-settings .eco-swatches{display:grid;grid-template-columns:repeat(auto-fit,minmax(58px,1fr));gap:16px 8px;padding:12px 0 8px}
.economy-settings button.eco-swatch{display:flex;flex-direction:column;align-items:center;justify-content:flex-start;gap:10px;min-width:0;min-height:74px;padding:5px 0;border:0;border-radius:10px;background:transparent;box-shadow:none;cursor:pointer;font:inherit;color:#79869A;text-align:center;-webkit-tap-highlight-color:transparent}
.economy-settings button.eco-swatch{appearance:none;-webkit-appearance:none;transition:none}
.economy-settings button.eco-swatch:hover,.economy-settings button.eco-swatch:active,.economy-settings button.eco-swatch:focus{background:transparent;box-shadow:none;filter:none}
.economy-settings button.eco-swatch:focus:not(:focus-visible){outline:none}
.economy-settings button.eco-swatch:focus-visible{outline:2px solid #548EE6;outline-offset:3px}
.economy-settings .eco-color-picker{-webkit-tap-highlight-color:transparent;color-scheme:light}
.economy-settings .eco-swatch:disabled{cursor:default;opacity:.6}
.economy-settings .eco-swatch-dot{width:36px;height:36px;border-radius:50%;flex-shrink:0;display:grid;place-items:center;position:relative;box-shadow:inset 0 0 0 1px #0000000A;transition:transform .15s ease}
.economy-settings .eco-swatch-dot:after{content:'';position:absolute;inset:-5px;border:2px solid #A7B3C5;border-radius:50%;opacity:0;pointer-events:none}
.economy-settings .eco-swatch.is-sel .eco-swatch-dot:after{opacity:1}
.economy-settings .eco-swatch.is-sel .eco-swatch-name{color:#253449;font-weight:600}
.economy-settings .eco-swatch:hover:not(:disabled) .eco-swatch-dot{transform:translateY(-2px)}
.economy-settings .eco-swatch-name{font-size:11px;font-weight:500;line-height:1.35;max-width:68px}
.economy-settings .eco-custom-color{margin:18px 0;padding-top:18px;border-top:1px solid #E7ECF3}
.economy-settings .eco-color-picker{position:relative;display:flex;align-items:center;gap:12px;padding:12px;border:1px solid #DFE5EE;border-radius:10px;cursor:pointer;background:#FAFBFD;overflow:hidden;width:100%;text-align:left;font:inherit;appearance:none;-webkit-tap-highlight-color:transparent}
.economy-settings .eco-color-picker strong{display:block;font-size:12px;font-weight:600;color:#253449}
.economy-settings .eco-color-picker span>span{display:block;font-size:11px;color:#7B879B;margin-top:3px}
.economy-settings .eco-custom-sample{width:32px;height:32px;border-radius:9px;flex-shrink:0;border:1px solid #00000014}
.economy-settings .eco-color-picker:disabled{cursor:default;opacity:.6}
.economy-settings .eco-color-picker:focus:not(:focus-visible){outline:none}
.economy-settings .eco-color-picker:focus-visible{outline:2px solid #548EE6;outline-offset:2px}


.economy-settings .eco-a4-preview{border:1px solid #E2E7EF;border-radius:12px;background:#fff;padding:16px;box-shadow:0 8px 24px -18px rgba(0,0,0,.35);font-size:9.5px;color:#111827;min-width:0}
.economy-settings .eco-a4-labels{display:grid;grid-template-columns:auto minmax(0,1fr) auto minmax(0,1fr);border:1px solid #D9DEE6}
.economy-settings .eco-a4-labels>div{padding:4px 6px;font-size:9px}
.economy-settings .eco-a4-customer{display:grid;grid-template-columns:1.5fr 1fr;gap:16px;padding:12px 0;border-top:1px solid #E5E7EB;border-bottom:1px solid #E5E7EB}
.economy-settings .eco-a4-customer>div:first-child{display:flex;flex-direction:column;gap:4px;font-size:9px}
.economy-settings .eco-preview-caption{font-size:7px;letter-spacing:.5px;color:#667085;font-weight:600}
.economy-settings .eco-preview-muted{color:#8993A1;font-size:8px}
.economy-settings .eco-a4-meta{display:grid;grid-template-columns:1fr auto;gap:5px 8px;font-size:8px;align-content:start}
.economy-settings .eco-a4-meta>span:nth-child(odd){color:#8993A1}
.economy-settings .eco-a4-meta>span:nth-child(even){text-align:right}
.economy-settings .eco-preview-total{display:flex;justify-content:space-between;padding:5px 8px;font-size:9px}
@media(max-width:480px){.economy-settings .eco-a4-customer{grid-template-columns:1fr;gap:10px}.economy-settings .eco-a4-preview{padding:12px}}
.economy-settings .eco-a4-row{display:grid;grid-template-columns:44px minmax(0,1fr) 70px;gap:6px;padding:4px 6px;font-size:9px}
.economy-settings .eco-a4-row .r{text-align:right}
@media(max-width:1000px){.economy-settings .eco-color-grid{grid-template-columns:minmax(0,1fr)}}
@media(max-width:480px){.economy-settings .eco-a4-labels{grid-template-columns:auto minmax(0,1fr)}.economy-settings .eco-swatches{grid-template-columns:repeat(4,minmax(0,1fr));gap:16px 6px}}
@media(max-width:1000px){.economy-settings .eco-settings-grid{grid-template-columns:minmax(0,1fr)}.economy-settings .eco-settings-grid>section{grid-column:1;grid-row:auto}}
@media(max-width:480px){.economy-settings .eco-brand [aria-label="Vista previa de la cabecera del comprobante"]{flex-direction:column;align-items:stretch}.economy-settings .eco-company h2{font-size:15px}.economy-settings .eco-settings-grid{gap:16px}}
`}</style>
    </ThemeProvider>
  );
}
